import React, { useState } from 'react';
import { FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import FormLabel from '@mui/material/FormLabel';
import { CompanyBenefit } from 'us-web-services';
import DateUtil from '../../../util/DateUtil';

interface Props {
  benefit: CompanyBenefit;
  setBenefit(benefit: CompanyBenefit): void;
}

export default function CompanyBenefitEditForm(props: Props) {
  const { benefit, setBenefit } = props;
  const [start, setStart] = useState(benefit.start);
  const [end, setEnd] = useState(benefit.end);
  const [description, setDescription] = useState(benefit.description);
  const [hasOon, setHasOon] = useState(benefit.hasOon || false);
  const [hasByop, setHasByop] = useState(benefit.hasByop || false);
  const [oonDescription, setOonDescription] = useState(benefit.oonDescription || '');
  const [byopDescription, setByopDescription] = useState(benefit.byopDescription || '');
  const [oonFormUrl, setOonFormUrl] = useState(benefit.oonFormUrl || '');

  const updateStart = date => {
    benefit.start = date ? date.format(DateUtil.midnightDateFormat) : undefined;
    setStart(date);
    setBenefit(benefit);
  };

  const updateEnd = date => {
    benefit.end = date ? date.format(DateUtil.midnightDateFormat) : undefined;
    setEnd(date);
    setBenefit(benefit);
  };

  const updateDescription = event => {
    benefit.description = event.target.value;
    setDescription(event.target.value);
    setBenefit(benefit);
  };

  const updateHasOon = event => {
    benefit.hasOon = event.target.checked;
    setHasOon(event.target.checked);
    setBenefit(benefit);

    if (!event.target.checked) {
      benefit.oonDescription = '';
      setOonDescription('');
      benefit.oonFormUrl = '';
      setOonFormUrl('');
    }
  };

  const updateHasByop = event => {
    benefit.hasByop = event.target.checked;
    setHasByop(event.target.checked);
    setBenefit(benefit);

    if (!event.target.checked) {
      benefit.byopDescription = '';
      setByopDescription('');
    }
  };

  const updateOonDescription = event => {
    benefit.oonDescription = event.target.value;
    setOonDescription(event.target.value);
    setBenefit(benefit);
  };

  const updateByopDescription = event => {
    benefit.byopDescription = event.target.value;
    setByopDescription(event.target.value);
    setBenefit(benefit);
  };

  const updateOonFormUrl = event => {
    benefit.oonFormUrl = event.target.value;
    setOonFormUrl(event.target.value);
    setBenefit(benefit);
  };

  return (
    <>
      <Typography variant='h6' gutterBottom>
        Benefit Details
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormLabel component='legend'>Start</FormLabel>
          <DatePicker
            disableMaskedInput
            disabled={benefit.statusId === 2}
            inputFormat={DateUtil.dateFormatSlashes}
            onChange={updateStart}
            value={start}
            renderInput={pickerProps => <TextField {...pickerProps} />}
          />
        </Grid>
        <Grid item xs={12}>
          <FormLabel component='legend'>End</FormLabel>
          <DatePicker
            disableMaskedInput
            inputFormat={DateUtil.dateFormatSlashes}
            onChange={updateEnd}
            value={end}
            renderInput={pickerProps => <TextField {...pickerProps} />}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            id='description'
            name='description'
            label='Description'
            value={description}
            onChange={updateDescription}
            fullWidth
            multiline
            minRows='5'
          />
        </Grid>

        <Grid item xs={12}>
          <Typography gutterBottom>
            Coverage Options
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={hasOon}
                onChange={updateHasOon}
                name='hasOon'
                color='primary'
              />
            }
            label='Out of Network (OON)'
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={hasByop}
                onChange={updateHasByop}
                name='hasByop'
                color='primary'
              />
            }
            label='Bring Your Own Provider (BYOP)'
          />
        </Grid>

        {(hasOon || hasByop) && (
          <Grid item xs={12}>
            <Typography gutterBottom style={{ marginTop: '16px' }}>
              Coverage Descriptions
            </Typography>
          </Grid>
        )}

        {hasOon && (
          <>
            <Grid item xs={12}>
              <TextField
                id='oonDescription'
                name='oonDescription'
                label='OON Description'
                value={oonDescription}
                onChange={updateOonDescription}
                fullWidth
                multiline
                minRows='3'
                helperText={`${oonDescription.length}/1000 characters`}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                id='oonFormUrl'
                name='oonFormUrl'
                label='OON Reimbursement Form URL'
                value={oonFormUrl}
                onChange={updateOonFormUrl}
                fullWidth
                type='url'
              />
            </Grid>
          </>
        )}

        {hasByop && (
          <Grid item xs={12}>
            <TextField
              id='byopDescription'
              name='byopDescription'
              label='BYOP Description'
              value={byopDescription}
              onChange={updateByopDescription}
              fullWidth
              multiline
              minRows='3'
              helperText={`${byopDescription.length}/1000 characters`}
              inputProps={{ maxLength: 1000 }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
}
